import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  faArrowUpRightFromSquare,
  faChartColumn,
  faChartLine,
  faEarthAmericas,
  faSparkles,
  faUsersGear,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Icon } from '../ui/icon';
import { hasRole, isV2User } from '../../utils/rbac';
import { getModuleUrl } from '../../utils/module-urls';
import type { TenantRbacDoc } from '../../api/useTenantRbacDoc';
import type { ModuleId, UserType } from '../../../types';

type AppId = ModuleId | 'admin_hub';

interface AppDescriptor {
  id: AppId;
  label: string;
  subtitle: string;
  icon: IconDefinition;
}

const APPS: Record<AppId, AppDescriptor> = {
  admin_hub: {
    id: 'admin_hub',
    label: 'Admin Hub',
    subtitle: 'Settings & users',
    icon: faUsersGear,
  },
  market_analyzer: {
    id: 'market_analyzer',
    label: 'Market Analyzer',
    subtitle: 'Trends & insights',
    icon: faEarthAmericas,
  },
  data_studio: {
    id: 'data_studio',
    label: 'Data Studio',
    subtitle: 'Build & visualise',
    icon: faChartColumn,
  },
  sales_copilot: {
    id: 'sales_copilot',
    label: 'Sales Copilot',
    subtitle: 'AI-assisted deals',
    icon: faChartLine,
  },
  ai_sales_copilot: {
    id: 'ai_sales_copilot',
    label: 'AI Sales Copilot',
    subtitle: 'Offerings, Account AI & Workflows',
    icon: faSparkles,
  },
};

const MODULE_ORDER: ModuleId[] = [
  'market_analyzer',
  'data_studio',
  'sales_copilot',
  'ai_sales_copilot',
];

/** The copilots in the order they win a tie, as in mk-admin-app's rbac.ts. */
const COPILOT_MODULES: ModuleId[] = ['sales_copilot', 'ai_sales_copilot'];

interface AppSwitcherProps {
  tenantId: string;
  user: UserType | null | undefined;
  rbacDoc?: TenantRbacDoc | null;
}

/**
 * v2 RBAC topbar app switcher. Ports mk-admin-app's
 * `src/common/components/topbar/app-switcher.tsx` for mk-addax. The dropdown
 * is hand-rolled (no Radix; mk-addax is React 16) but the markup, classnames,
 * CSS, icon set, and gating logic match exactly so the two admins look and
 * behave identically for v2 users.
 *
 * Hidden entirely for v1 users (no `user.rbac`) unless the caller is a
 * SuperKudu with a hydrated tenant RBAC doc.
 */
export function AppSwitcher({ tenantId, user, rbacDoc }: AppSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setMenuPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 8, left: rect.left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        triggerRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (!isV2User(user) && !(user?.superKudu && !!rbacDoc)) return null;

  // Same source-of-truth precedence as mk-admin-app: user's own claim wins;
  // fall back to the tenant doc so SuperKudu impersonating a v1 user still
  // sees the org's modules.
  const purchasedModules: ModuleId[] =
    user?.rbac?.purchasedModules ?? rbacDoc?.purchased_modules ?? [];
  const purchased = new Set<ModuleId>(purchasedModules);
  // Sales-Copilot-only orgs still get Data Studio: the Org Admin needs
  // Computations + Aggregations there to configure the Copilot scoring pipeline.
  if (purchased.has('sales_copilot')) purchased.add('data_studio');
  // Admin Hub is only for tenant admins (RGI-1335); standard users never see it.
  const showAdminHub = !!user?.superKudu || hasRole(user, 'orgAdmin');
  // An org holding both copilots is offered one, MSI first, as in
  // mk-admin-app's `resolveApps` (RGI-2287). SuperKudu sees both.
  const copilot = COPILOT_MODULES.find((m) => purchased.has(m));
  const apps: AppDescriptor[] = [
    ...(showAdminHub ? [APPS.admin_hub] : []),
    ...MODULE_ORDER.filter((m) => {
      if (user?.superKudu) return true;
      if (COPILOT_MODULES.includes(m)) return m === copilot;
      return purchased.has(m);
    }).map((m) => APPS[m]),
  ];

  const current = APPS.admin_hub;

  const handleSelect = (app: AppDescriptor) => {
    setIsOpen(false);
    if (app.id === 'admin_hub') return;
    const url = getModuleUrl(app.id as ModuleId, { tenantId, user });
    if (url) window.location.href = url;
  };

  const handleOpenInNewTab = (url: string) => {
    setIsOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const menuContent =
    isOpen && menuPosition ? (
      <div
        ref={menuRef}
        id="topbar-app-switcher-menu"
        className="app-switcher-menu"
        style={{
          position: 'fixed',
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: 260,
          zIndex: 10050,
          backgroundColor: '#ffffff',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
          boxShadow:
            '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
        }}
      >
        <div className="app-switcher-menu__label">Select Application</div>
        {apps.map((app) => {
          const isCurrent = app.id === current.id;
          const url = isCurrent
            ? null
            : getModuleUrl(app.id as ModuleId, { tenantId, user });
          return (
            <div
              key={app.id}
              role="button"
              tabIndex={0}
              className="app-switcher-menu__item"
              data-current={isCurrent ? 'true' : undefined}
              onClick={() => handleSelect(app)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(app);
                }
              }}
            >
              <span className="app-switcher-menu__icon">
                <Icon icon={app.icon} />
              </span>
              <span className="app-switcher-menu__text">
                <span className="app-switcher-menu__name">{app.label}</span>
                <span className="app-switcher-menu__subtitle">
                  {app.subtitle}
                </span>
              </span>
              {url && (
                <button
                  type="button"
                  className="app-switcher-menu__open"
                  aria-label={`Open ${app.label} in a new tab`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenInNewTab(url);
                  }}
                >
                  <Icon icon={faArrowUpRightFromSquare} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        id="topbar-app-switcher-trigger"
        className="app-switcher-trigger"
        type="button"
        data-state={isOpen ? 'open' : 'closed'}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className="app-switcher-trigger__label">{current.label}</span>
        <Icon icon={faCaretDown} className="app-switcher-trigger__chevron" />
      </button>
      {menuContent && ReactDOM.createPortal(menuContent, document.body)}
    </>
  );
}
